// @ts-check
import React, { createContext, useCallback, useContext, useReducer, useEffect, useRef } from 'react';
import './Toast.css';

/**
 * @typedef {'success' | 'error' | 'info'} ToastKind
 * @typedef {{ id: string, kind: ToastKind, message: string }} Toast
 */

const ToastContext = createContext(/** @type {null | { addToast: (t: { kind: ToastKind, message: string }) => void, dismissToast: (id: string) => void }} */ (null));

const AUTO_DISMISS_MS = 5000;
let nextId = 0;

/**
 * @param {Toast[]} state
 * @param {{ type: 'add', toast: Toast } | { type: 'remove', id: string }} action
 */
function reducer(state, action) {
  switch (action.type) {
    case 'add':
      return [...state, action.toast];
    case 'remove':
      return state.filter((t) => t.id !== action.id);
    default:
      return state;
  }
}

/**
 * Wraps the app. Provides addToast/dismissToast via useToast() and renders the
 * live region that announces toasts to screen readers.
 *
 * @param {{ children: React.ReactNode }} props
 */
export function ToastProvider({ children }) {
  const [toasts, dispatch] = useReducer(reducer, []);
  const timersRef = useRef(/** @type {Map<string, ReturnType<typeof setTimeout>>} */ (new Map()));

  const dismissToast = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    dispatch({ type: 'remove', id });
  }, []);

  const addToast = useCallback(
    /** @param {{ kind: ToastKind, message: string }} input */
    ({ kind, message }) => {
      nextId += 1;
      const id = `toast-${nextId}`;
      dispatch({ type: 'add', toast: { id, kind, message } });

      // Errors are sticky — operator must dismiss explicitly.
      if (kind !== 'error') {
        const timer = setTimeout(() => {
          timersRef.current.delete(id);
          dispatch({ type: 'remove', id });
        }, AUTO_DISMISS_MS);
        timersRef.current.set(id, timer);
      }
    },
    [],
  );

  // Clean up any pending timers on unmount.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={{ addToast, dismissToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

/**
 * Hook for components that emit toasts.
 *
 * @returns {{ addToast: (t: { kind: ToastKind, message: string }) => void, dismissToast: (id: string) => void }}
 */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a <ToastProvider>');
  }
  return ctx;
}

const ICONS = {
  success: '✓',
  error: '!',
  info: 'i',
};

/**
 * @param {{ toasts: Toast[], onDismiss: (id: string) => void }} props
 */
function ToastContainer({ toasts, onDismiss }) {
  return (
    <div className="toast-container" aria-live="polite" aria-atomic="false">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`toast toast--${t.kind}`}
          role={t.kind === 'error' ? 'alert' : 'status'}
        >
          <span className="toast__icon" aria-hidden="true">
            {ICONS[t.kind]}
          </span>
          <span className="toast__message">{t.message}</span>
          <button
            type="button"
            className="toast__dismiss"
            onClick={() => onDismiss(t.id)}
            aria-label="Dismiss notification"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
